import { useRef, useCallback, useState, useEffect } from 'react';

interface NotebookSounds {
  playPencilScratch: () => void;
  playPageFlip: () => void;
  playPaperCrinkle: () => void;
  playErase: () => void;
  playHighlight: () => void;
  playClick: () => void;
  setVolume: (volume: number) => void;
  mute: () => void;
  unmute: () => void;
  isMuted: boolean;
  volume: number;
}

export const useNotebookSounds = (): NotebookSounds => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolumeState] = useState(0.15);

  // Initialize audio context on first user interaction
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          audioContextRef.current = new AudioContextClass();
        }
      } catch (e) {
        console.warn('Web Audio API not supported');
      }
    }
    return audioContextRef.current;
  }, []);

  // Resume audio context if suspended (browsers require user gesture)
  const ensureAudioContextRunning = useCallback(async () => {
    const ctx = initAudioContext();
    if (ctx && ctx.state === 'suspended') {
      await ctx.resume();
    }
    return ctx;
  }, [initAudioContext]);

  // Generate pencil scratch sound using filtered noise
  const playPencilScratch = useCallback(async () => {
    if (isMuted) return;
    const ctx = await ensureAudioContextRunning();
    if (!ctx) return;

    try {
      const duration = 0.08 + Math.random() * 0.04;
      const bufferSize = Math.floor(ctx.sampleRate * duration);
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      // Generate scratchy noise
      for (let i = 0; i < bufferSize; i++) {
        const envelope = Math.exp(-i / (bufferSize * 0.3));
        data[i] = (Math.random() * 2 - 1) * envelope;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      // Bandpass filter for pencil-like sound
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 2000 + Math.random() * 1000;
      filter.Q.value = 2;

      // Highpass to remove low rumble
      const highpass = ctx.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.value = 800;

      const gain = ctx.createGain();
      gain.gain.value = volume * 0.4;

      source.connect(filter).connect(highpass).connect(gain).connect(ctx.destination);
      source.start();
      source.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silent fail
    }
  }, [isMuted, volume, ensureAudioContextRunning]);

  // Generate page flip whoosh sound
  const playPageFlip = useCallback(async () => {
    if (isMuted) return;
    const ctx = await ensureAudioContextRunning();
    if (!ctx) return;

    try {
      const duration = 0.3;
      const bufferSize = Math.floor(ctx.sampleRate * duration);
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      // Generate swoosh noise with frequency sweep
      for (let i = 0; i < bufferSize; i++) {
        const t = i / bufferSize;
        const envelope = Math.sin(t * Math.PI) * Math.exp(-t * 3);
        data[i] = (Math.random() * 2 - 1) * envelope;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      // Lowpass filter with frequency sweep for whoosh effect
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(3000, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + duration);

      const gain = ctx.createGain();
      gain.gain.value = volume * 0.3;

      source.connect(filter).connect(gain).connect(ctx.destination);
      source.start();
      source.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silent fail
    }
  }, [isMuted, volume, ensureAudioContextRunning]);

  // Generate paper crinkle sound
  const playPaperCrinkle = useCallback(async () => {
    if (isMuted) return;
    const ctx = await ensureAudioContextRunning();
    if (!ctx) return;

    try {
      const duration = 0.15;
      const bufferSize = Math.floor(ctx.sampleRate * duration);
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      // Crinkle noise with random bursts
      for (let i = 0; i < bufferSize; i++) {
        const t = i / bufferSize;
        const burst = Math.random() > 0.7 ? 1 : 0.3;
        const envelope = Math.exp(-t * 5);
        data[i] = (Math.random() * 2 - 1) * envelope * burst;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 2000;

      const gain = ctx.createGain();
      gain.gain.value = volume * 0.2;

      source.connect(filter).connect(gain).connect(ctx.destination);
      source.start();
      source.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silent fail
    }
  }, [isMuted, volume, ensureAudioContextRunning]);

  // Generate eraser sound
  const playErase = useCallback(async () => {
    if (isMuted) return;
    const ctx = await ensureAudioContextRunning();
    if (!ctx) return;

    try {
      const duration = 0.12;
      const bufferSize = Math.floor(ctx.sampleRate * duration);
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < bufferSize; i++) {
        const envelope = 1 - (i / bufferSize);
        data[i] = (Math.random() * 2 - 1) * envelope * 0.5;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 1500;

      const gain = ctx.createGain();
      gain.gain.value = volume * 0.25;

      source.connect(filter).connect(gain).connect(ctx.destination);
      source.start();
      source.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silent fail
    }
  }, [isMuted, volume, ensureAudioContextRunning]);

  // Generate highlighter marker sound
  const playHighlight = useCallback(async () => {
    if (isMuted) return;
    const ctx = await ensureAudioContextRunning();
    if (!ctx) return;

    try {
      const duration = 0.1;
      const bufferSize = Math.floor(ctx.sampleRate * duration);
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      // Squeaky marker sound
      for (let i = 0; i < bufferSize; i++) {
        const t = i / ctx.sampleRate;
        const freq = 800 + Math.sin(t * 50) * 200;
        const envelope = Math.exp(-i / (bufferSize * 0.5));
        data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.2;
        data[i] += (Math.random() * 2 - 1) * envelope * 0.1;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      const gain = ctx.createGain();
      gain.gain.value = volume * 0.15;

      source.connect(gain).connect(ctx.destination);
      source.start();
      source.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silent fail
    }
  }, [isMuted, volume, ensureAudioContextRunning]);

  // Simple click sound
  const playClick = useCallback(async () => {
    if (isMuted) return;
    const ctx = await ensureAudioContextRunning();
    if (!ctx) return;

    try {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(600, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.05);

      gain.gain.setValueAtTime(volume * 0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.05);
    } catch (e) {
      // Silent fail
    }
  }, [isMuted, volume, ensureAudioContextRunning]);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  }, []);

  const mute = useCallback(() => setIsMuted(true), []);
  const unmute = useCallback(() => setIsMuted(false), []);

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    playPencilScratch,
    playPageFlip,
    playPaperCrinkle,
    playErase,
    playHighlight,
    playClick,
    setVolume,
    mute,
    unmute,
    isMuted,
    volume,
  };
};

export default useNotebookSounds;
